/**
 * LinkMentionNode - Tiptap extension for @link mentions
 *
 * Creates inline atom nodes for template links (e.g., @termsLink, @privacyPolicy)
 * These render as styled chips in the editor and serialize to @linkName format.
 *
 * Based on MentionChipNode pattern from RichMentionEditor.js
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { LinkMentionChip } from '../components/LinkMentionChip';

export interface LinkMentionNodeAttrs {
  /** Link identifier/name (e.g., "termsLink") */
  id: string;
  /** Link URL */
  url: string;
  /** Optional label tag wrapping this mention (e.g., "highlight") */
  labelTag: string | null;
}

export const LinkMentionNode = Node.create({
  name: 'linkMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-link-id'),
        renderHTML: (attributes) => ({
          'data-link-id': attributes.id,
        }),
      },
      url: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-link-url'),
        renderHTML: (attributes) => ({
          'data-link-url': attributes.url,
        }),
      },
      labelTag: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label-tag'),
        renderHTML: (attributes) => {
          if (!attributes.labelTag) return {};
          return { 'data-label-tag': attributes.labelTag };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-link-id]',
        getAttrs: (domNode) => {
          const dom = domNode as HTMLElement;
          return {
            id: dom.getAttribute('data-link-id'),
            url: dom.getAttribute('data-link-url') || '',
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'link-mention',
        'data-type': 'linkMention',
      }),
      `@${HTMLAttributes['data-link-id'] || ''}`,
    ];
  },

  /**
   * Serialize to plain text format: @linkName
   * This is used when extracting text content from the editor
   */
  renderText({ node }) {
    return `@${node.attrs.id || ''}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkMentionChip);
  },
});

export default LinkMentionNode;
